import { publishPosLoyaltyEarnAck, type CanonicalLoyaltyEarnAck } from "../loyalty/loyalty-earn-ack";
import type { PromotionClaimEvidence } from "../promotion/promotion-claim-session";

export type PosSaleGatewayLine = {
  line_id: string;
  menu_item_id: string;
  menu_variant_id: string | null;
  quantity: number;
  local_unit_price: number;
  price_version_id: string;
  modifier_option_ids: string[];
};

export type PosSalePromotionClaimEvidence = PromotionClaimEvidence;

export type PosSaleSyncInput = {
  deviceSecret: string;
  sessionToken: string;
  localTransactionId: string;
  lines: PosSaleGatewayLine[];
  catalogRevision: number;
  localTotal: number;
  localCommittedAt: string;
  executionMode: "ONLINE" | "OFFLINE";
  paymentMethodCode: string;
  receivingDestinationId: string;
  requestKey: string;
  clientContext?: Record<string, unknown>;
  promotionClaimEvidence?: PosSalePromotionClaimEvidence[];
};

export type CanonicalPosSalePayment = {
  payment_id: string;
  payment_no: number;
  payment_status: string;
  payment_method_code: string;
  payment_method_name: string;
  provider_code: string | null;
  currency_code: string;
  amount: number;
  tendered_amount: number | null;
  change_amount: number | null;
  authorized_at: string | null;
  captured_at: string | null;
  settled_at: string | null;
  request_key: string;
  payment_snapshot: Record<string, unknown>;
};

export type CanonicalPosSalePromotionClaim = {
  claim_instance_id: string;
  claim_definition_id: string;
  promotion_version_id: string;
  claim_status: string;
  consumed_at: string | null;
  consumed_order_id: string | null;
  credential_hint: string | null;
  binding_mode: string;
};

export type PosSaleSyncResult = {
  ok: boolean;
  contract: string;
  result_status?: string;
  idempotent_replay?: boolean;
  atomic?: boolean;
  order_id: string;
  order_no: string;
  order_status: string;
  organization_id: string;
  branch_id: string;
  commerce_channel_id: string;
  commerce_node_id: string;
  test_run_id: string | null;
  currency_code: string;
  grand_total: number;
  paid_amount: number;
  confirmed_at: string;
  paid_at: string;
  root_request_key: string;
  promotion_claims?: CanonicalPosSalePromotionClaim[];
  payment: CanonicalPosSalePayment;
  loyalty_earn?: CanonicalLoyaltyEarnAck | null;
};

export type PosSaleGatewayResult = {
  ok: boolean;
  data?: PosSaleSyncResult;
  error?: string;
};

export class PosSaleGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async sync(input: PosSaleSyncInput): Promise<PosSaleGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/sale`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          local_transaction_id: input.localTransactionId,
          lines: input.lines,
          catalog_revision: input.catalogRevision,
          local_total: input.localTotal,
          tendered_amount: null,
          local_change_amount: null,
          local_committed_at: input.localCommittedAt,
          execution_mode: input.executionMode,
          payment_method_code: input.paymentMethodCode,
          receiving_destination_id: input.receivingDestinationId,
          verification_source: "STAFF_CONFIRMED",
          verification_note: null,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
          promotion_claim_evidence: input.promotionClaimEvidence ?? [],
        }),
        signal: controller.signal,
        credentials: "omit",
      });

      const payload = (await response.json().catch(() => null)) as PosSaleGatewayResult | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      publishPosLoyaltyEarnAck({
        orderId: payload.data.order_id,
        orderNo: payload.data.order_no,
        loyaltyEarn: payload.data.loyalty_earn ?? null,
      });
      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
